'use client';

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { useSession, signIn } from 'next-auth/react';
import { Download, Printer, Shapes, Image as ImageIcon, Loader2, LogIn, Lightbulb, Gift } from 'lucide-react';
import { openBuyTokens, useTrack } from '@owlcat/app-shell';
import { classicMazeId, elapsedSeconds, mazeKindFor, mazeKindForMode } from '@/contexts/games/analytics/mazeAnalytics';
import { Button } from '@/shared/components/ui/button';
import { GameLayout } from '@/contexts/games/components/common/GameLayout';
import { DifficultySelector } from '@/contexts/games/components/common/DifficultySelector';
import { GameRulesModal } from '@/contexts/games/components/common/GameRulesModal';
import { GameVictoryModal } from '@/contexts/games/components/common/GameVictoryModal';
import { useGameTimer } from '@/contexts/games/hooks/useGameTimer';
import { useGameSettings } from '@/contexts/games/hooks/useGameSettings';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { GameDifficulty, GameResult, DEFAULT_GAME_SETTINGS } from '@/contexts/games/types';
import { MazeGameSettings, MazeMode, VoronoiMazeState, VoronoiMazeData, Position } from './types/maze-types';
import { setMazeEmojis } from './config/mazeEmojis';
import { MazeCanvas } from './MazeCanvas';
import { MazeRules } from './MazeRules';
import { MazeColorSelector } from './MazeColorSelector';
import { MazeModeSelector } from './MazeModeSelector';
import { TemplateGallery } from './TemplateGallery';
import { ImageUploadPanel } from './ImageUploadPanel';
import { SavedMazesGallery } from './SavedMazesGallery';
import { MazePrintPreview } from './components/MazePrintPreview';
import { enqueueMazeGeneration } from '@/contexts/games/actions/enqueueMazeGeneration';
import { reEnqueueImageMaze } from '@/contexts/games/actions/reEnqueueImageMaze';
import { getSavedMaze } from '@/contexts/games/actions/getSavedMaze';
import { readMazeResult } from '@/contexts/games/actions/readMazeResult';
import { abandonMazeGeneration } from '@/contexts/games/actions/abandonMazeGeneration';
import { useMazeImageQuota } from '@/contexts/subscriptions/hooks/useMazeImageQuota';
import type { SavedMaze } from '@/contexts/games/types';
import { availableColors } from './helpers/mazeUtils';
import { loadTemplateConfig } from './helpers/templateLoader';
import { recarveMaze, findSolutionPath } from './helpers/mazeRecarver';
import { tessellateFromContour, densifyForScreen } from './helpers/voronoiTessellate';
import { fitGeometryToBox } from './helpers/voronoiFit';
import { VoronoiMazeRenderer } from './helpers/voronoiMazeRenderer';
import { getTemplateById, getTemplatesWithConfig, getTemplateConfigPath } from './assets/templates/maze-template-registry';

/** Async generation flow phase: idle -> processing -> playing */
type GenerationPhase = 'idle' | 'processing' | 'playing';


// Default settings for Maze Game
const MAZE_DEFAULT_SETTINGS: MazeGameSettings = {
  ...DEFAULT_GAME_SETTINGS,
  lineColor: availableColors.blue,
  showGrid: false,
  animationSpeed: 1,
  shapeStyle: 'orthogonal',
  curvature: 0,
  mazeMode: 'rectangular',
  imageOpacity: 0.3,
};

const RANDOM_SHAPE_STYLES: Array<'orthogonal' | 'organic' | 'circular'> = [
  'orthogonal',
  'organic',
  'circular',
];

/** Update URL search params without page reload. */
function updateUrlParams(params: Record<string, string | undefined>) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  // Clear maze-related params first
  url.searchParams.delete('mode');
  url.searchParams.delete('templateId');
  url.searchParams.delete('difficulty');
  url.searchParams.delete('mazeId');
  // Set new params
  for (const [key, value] of Object.entries(params)) {
    if (value) url.searchParams.set(key, value);
  }
  window.history.pushState({}, '', url.toString());
}

/** Compute canvas size from viewport, respecting aspect ratio. */
/**
 * The box the maze canvas may fill: the REAL play area (the flex-1 region under
 * the mode bar, measured from the DOM — it already excludes the fleet sidebar,
 * the mobile top bar and the game header), with a fallback to viewport math
 * before mount. Voronoi coordinates are scaled to this box, so it decides the
 * on-screen passage width too.
 */
function measurePlayArea(): { maxW: number; maxH: number } {
  if (typeof window === 'undefined') return { maxW: 800, maxH: 600 };
  const el = document.querySelector('[data-testid="maze-play-area"]');
  const rect = el?.getBoundingClientRect();
  if (rect && rect.width > 100 && rect.height > 100) {
    return { maxW: rect.width - 8, maxH: rect.height - 8 };
  }
  const isMobile = window.innerWidth < 768;
  return {
    maxW: window.innerWidth - (isMobile ? 16 : 340),
    maxH: window.innerHeight - (isMobile ? 160 : 170),
  };
}

/**
 * A saved maze's geometry was tessellated for `maze.difficulty`. When the player
 * is on another level and the worker did not produce that level (older maze,
 * no `geometryUrls`), re-tessellate the contour locally so the level actually
 * changes the cell count; otherwise the saved geometry is used as is (the
 * per-level worker file, when present, is fetched by handleDifficultyChange).
 */
function geometryForLevel(mazeData: VoronoiMazeData, maze: SavedMaze, difficulty: GameDifficulty): VoronoiMazeData {
  if (maze.difficulty === difficulty || maze.geometryUrls?.[difficulty] || !mazeData.contour) {
    return densifyForScreen(mazeData, difficulty);
  }
  return (
    tessellateFromContour(mazeData.contour, difficulty, {
      aspectRatio: mazeData.aspectRatio,
      geometryTransform: mazeData.geometryTransform,
    }) ?? mazeData
  );
}

/** Fit a worker geometry (0..1 normalized) into the play area; returns the canvas size. */
function fitToPlayArea(mazeData: VoronoiMazeData): { w: number; h: number } {
  const { maxW, maxH } = measurePlayArea();
  const { width, height } = fitGeometryToBox(mazeData, maxW, maxH);
  return { w: width, h: height };
}

/** Pick a random mode from rectangular, radial, and template (33% each).
 *  When rectangular is picked, also randomize the shape style. */
function pickRandomMode(): { mode: MazeMode; templateId?: string; shapeStyle?: 'orthogonal' | 'organic' | 'circular' } {
  const roll = Math.random();
  if (roll < 1 / 3) {
    const shapeStyle = RANDOM_SHAPE_STYLES[Math.floor(Math.random() * RANDOM_SHAPE_STYLES.length)];
    return { mode: 'rectangular', shapeStyle };
  }
  if (roll < 2 / 3) return { mode: 'radial' };

  // Pick a random pre-computed template
  const withConfig = getTemplatesWithConfig();
  if (withConfig.length === 0) return { mode: 'rectangular' };
  const tpl = withConfig[Math.floor(Math.random() * withConfig.length)];
  return { mode: 'template', templateId: tpl.id };
}

/**
 * Главный компонент игры лабиринт
 * Интегрирован с универсальной игровой архитектурой
 */
interface MazeGameProps {
  initialEmojis?: { start: string; end: string };
}

export default function MazeGame({ initialEmojis }: MazeGameProps = {}) {
  // Apply synchronously during render so MazeCanvas's first regenerate
  // (which fires from a child useEffect, before a parent useEffect) already
  // sees the admin-configured values instead of the module fallbacks.
  if (initialEmojis) {
    setMazeEmojis(initialEmojis.start, initialEmojis.end);
  }

  const t = useTranslations();
  const { data: session, status: sessionStatus } = useSession();
  // A native session whose accounts epoch was bumped (re-login elsewhere) still
  // resolves as 'authenticated' with user.id === '' (the jwt callback blanks the
  // claims, it does not drop the cookie) — treat that as signed-out so the panel
  // shows "sign in" instead of an upload whose reserve then fails server-side.
  const isAuthenticated =
    sessionStatus === 'authenticated' && !!(session?.user as { id?: string } | undefined)?.id;
  // Photo-maze quota in MAZES (hidden tokens): undefined = loading, null = unknown
  // (let the server reserve decide), 0 = gift spent → top-up panel.
  const { remaining: mazeQuota, giftMazes, refresh: refreshMazeQuota, setRemaining: setMazeQuota } = useMazeImageQuota();
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;

  // Game settings with persistence
  const { settings, updateSettings } = useGameSettings<MazeGameSettings>('maze', MAZE_DEFAULT_SETTINGS);
  // Latest settings for async work started by the mount effect: that effect's
  // closure still holds the DEFAULTS (the stored settings land in state one
  // commit later), so a deep-linked saved maze used to open at the default
  // level while the selector showed the stored one.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // Whether the user explicitly chose a maze mode (vs. auto-randomized)
  const [isExplicitMode, setIsExplicitMode] = useState(false);

  // Ref to hold handleTemplateSelect so it can be called before definition
  const handleTemplateSelectRef = useRef<(id: string) => void>(() => {});

  // Restore state from URL params on page load/refresh
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // ?mazeId=UUID — load saved maze from DB
    const mazeIdParam = searchParams?.get('mazeId');
    if (mazeIdParam) {
      // A saved PHOTO maze deep-link: same state slots as the gallery pick
      // (imageVoronoiState / imagePhase) — the image-mode canvas gates on those.
      // (This used to feed the TEMPLATE slots and the saved maze never rendered.)
      // `?difficulty=` wins; otherwise the stored level (read at fetch time).
      const linkedDifficulty = searchParams?.get('difficulty') as GameDifficulty | null;
      updateSettings(linkedDifficulty ? { mazeMode: 'image', difficulty: linkedDifficulty } : { mazeMode: 'image' });
      setIsExplicitMode(true);
      setImagePhase('processing');
      setImageError(null);
      getSavedMaze(mazeIdParam)
        .then((maze) => {
          if (!maze) {
            setImagePhase('idle');
            return;
          }
          return fetch(mediaSrc(maze.geometryBlobUrl))
            .then((res) => res.json())
            .then((mazeData: VoronoiMazeData) => {
              const { w, h } = fitToPlayArea(mazeData);
              const level = linkedDifficulty ?? settingsRef.current.difficulty;
              beginMaze(maze.id);

              const source = geometryForLevel(mazeData, maze, level);
              originalMazeDataRef.current = source;
              imageGeometryUrlsRef.current = maze.geometryUrls ?? null;
              const recarved = recarveMaze(source, { difficulty: level });
              setImageVoronoiState({
                mazeData: recarved,
                canvasSize: { width: w, height: h },
                pathSegments: [],
                isCompleted: false,
                backgroundImageUrl: maze.sourceImageBlobUrl || undefined,
              });
              setImagePhase('playing');

              // Preload background image
              if (maze.sourceImageBlobUrl) {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => {
                  setImageVoronoiState(prev => prev ? { ...prev, backgroundImage: img } : null);
                };
                img.src = mediaSrc(maze.sourceImageBlobUrl);
              }
            });
        })
        .catch(() => setImagePhase('idle'));
      return;
    }

    // ?mode=template&templateId=tpl-cat — load specific template
    const modeParam = searchParams?.get('mode') as MazeMode | null;
    const templateIdParam = searchParams?.get('templateId');
    const difficultyParam = searchParams?.get('difficulty') as GameDifficulty | null;

    if (modeParam) {
      const newSettings: Partial<MazeGameSettings> = { mazeMode: modeParam };
      if (difficultyParam) newSettings.difficulty = difficultyParam;
      updateSettings(newSettings);
      setIsExplicitMode(true);

      if (modeParam === 'template' && templateIdParam) {
        setTimeout(() => handleTemplateSelectRef.current(templateIdParam), 0);
      } else if (modeParam === 'image') {
        setImagePhase('idle');
      }
      return;
    }

    // No URL params — randomize
    if (!isExplicitMode) {
      const pick = pickRandomMode();
      updateSettings({ mazeMode: pick.mode, ...(pick.shapeStyle ? { shapeStyle: pick.shapeStyle } : {}) });
      if (pick.mode === 'template' && pick.templateId) {
        const templateId = pick.templateId;
        setTimeout(() => handleTemplateSelectRef.current(templateId), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle browser back/forward: re-read URL params and restore mode
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const mode = params.get('mode') as MazeMode | null;
      const templateId = params.get('templateId');
      const difficulty = params.get('difficulty') as GameDifficulty | null;

      if (difficulty) updateSettings({ difficulty });

      if (mode === 'template' && templateId) {
        updateSettings({ mazeMode: 'template' });
        handleTemplateSelectRef.current(templateId);
      } else if (mode === 'image') {
        updateSettings({ mazeMode: 'image' });
        setImagePhase('idle');
        setImageVoronoiState(null);
      } else if (mode === 'rectangular' || mode === 'radial') {
        updateSettings({ mazeMode: mode });
        mazeRef.current?.regenerate();
      } else if (!mode) {
        // Back to initial state — randomize
        const pick = pickRandomMode();
        updateSettings({ mazeMode: pick.mode, ...(pick.shapeStyle ? { shapeStyle: pick.shapeStyle } : {}) });
        if (pick.mode === 'template' && pick.templateId) {
          handleTemplateSelectRef.current(pick.templateId);
        }
      }

      setIsCompleted(false);
      setGameResult(null);
      setShowSolution(false);
      timer.reset();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Состояние игры
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [mazeState, setMazeState] = useState<any>(null);
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);

  // Template mode state
  const [templatePhase, setTemplatePhase] = useState<GenerationPhase>('idle');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [voronoiMazeState, setVoronoiMazeState] = useState<VoronoiMazeState | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const pendingMazeIdRef = useRef<string | null>(null);

  // Show solution path overlay
  const [showSolution, setShowSolution] = useState(false);

  // First-visit discoverability hint (dismissed persists in localStorage)
  const [showCapabilityHint, setShowCapabilityHint] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem('maze-caps-hint-dismissed') !== '1') {
        setShowCapabilityHint(true);
      }
    } catch {
      setShowCapabilityHint(true);
    }
  }, []);
  const dismissCapabilityHint = useCallback(() => {
    setShowCapabilityHint(false);
    try {
      localStorage.setItem('maze-caps-hint-dismissed', '1');
    } catch {
      /* ignore */
    }
  }, []);

  // Store original (uncarved) maze data so re-carving always starts from full adjacency
  const originalMazeDataRef = useRef<VoronoiMazeData | null>(null);
  // Per-difficulty geometry urls of the CURRENT image maze (worker tessellates
  // every level in one paid job) → a difficulty switch is a fetch, not a re-queue.
  const imageGeometryUrlsRef = useRef<Record<string, string> | null>(null);

  // Image mode state
  const [imagePhase, setImagePhase] = useState<GenerationPhase>('idle');
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageVoronoiState, setImageVoronoiState] = useState<VoronoiMazeState | null>(null);

  // Ссылка на методы лабиринта
  const mazeRef = useRef<{ regenerate: () => void; download: () => void; getState: () => any; getCanvas: () => HTMLCanvasElement | null } | null>(null);

  // Таймер игры
  const timer = useGameTimer();

  // ── Fleet analytics (@owlcat/app-shell FleetEvents; consent-gated + prod-only inside) ──
  const track = useTrack();
  // Id of the maze on screen for maze_completed / maze_shared: the persisted id of a
  // photo/shape maze, the template id of a pre-computed shape, null for classic modes.
  const currentMazeIdRef = useRef<string | null>(null);
  // Times the solution overlay was switched ON for the current maze (= `hints`).
  const solutionPeeksRef = useRef(0);
  const beginMaze = useCallback((id: string | null) => {
    currentMazeIdRef.current = id;
    solutionPeeksRef.current = 0;
  }, []);
  const eventMazeId = useCallback(
    (mode: MazeMode) =>
      mazeKindForMode(mode) === 'classic' ? classicMazeId(mode) : (currentMazeIdRef.current ?? classicMazeId(mode)),
    [],
  );
  const toggleSolution = useCallback(() => {
    setShowSolution((prev) => {
      if (!prev) solutionPeeksRef.current += 1;
      return !prev;
    });
  }, []);
  // A worker-generated maze landed (SSE nudge or poll) → maze_generated. `gift` is
  // NOT knowable client-side (accounts consumes lots server-side), so it is false.
  const trackGenerated = useCallback(
    (mazeId: string, mazeType: string | undefined, isImage?: boolean) => {
      beginMaze(mazeId);
      track('maze_generated', {
        maze_id: mazeId,
        kind: mazeKindFor(mazeType, isImage),
        difficulty: settingsRef.current.difficulty,
        gift: false,
      });
    },
    [beginMaze, track],
  );
  // Out-of-mazes panel shown (gift spent / top-up needed) → allowance_exhausted, once
  // per appearance of the panel (not per render).
  const allowanceExhausted =
    settings.mazeMode === 'image' && imagePhase === 'idle' && isAuthenticated && mazeQuota === 0;
  const exhaustedShownRef = useRef(false);
  useEffect(() => {
    if (!allowanceExhausted) {
      exhaustedShownRef.current = false;
      return;
    }
    if (exhaustedShownRef.current) return;
    exhaustedShownRef.current = true;
    track('allowance_exhausted', { app_id: 'games', action: 'maze_generate' });
  }, [allowanceExhausted, track]);

  // Compute solution path for the active Voronoi maze when showSolution is toggled on
  const activeMazeData = settings.mazeMode === 'template'
    ? voronoiMazeState?.mazeData
    : settings.mazeMode === 'image'
      ? imageVoronoiState?.mazeData
      : undefined;

  const solutionPath: number[] | null = useMemo(() => {
    if (!showSolution || !activeMazeData) return null;
    return findSolutionPath(activeMazeData);
  }, [showSolution, activeMazeData]);

  // Fetch geometry + render a freshly-generated maze into the right mode's state.
  // Shared by the SSE nudge AND the readMazeResult poll fallback so a dropped SSE
  // still shows the maze. `isImage` (when known from the call site) wins over the
  // heuristic; otherwise fall back to the current phase/mode (SSE parity).
  const renderGeneratedMaze = useCallback((args: {
    geometryUrl: string;
    /** Per-difficulty geometry urls (already proxied) when the worker produced them. */
    geometryUrls?: Record<string, string>;
    mazeType?: string;
    imageUrl?: string;
    aspectRatio?: number;
    isImage?: boolean;
    /** Difficulty the geometry was tessellated for (defaults to the current setting). */
    difficulty?: GameDifficulty;
  }) => {
    const { geometryUrl, mazeType, imageUrl } = args;
    const difficulty = args.difficulty ?? settings.difficulty;
    const payloadAspectRatio = args.aspectRatio || 1.0;

    // Fetch maze geometry JSON from blob storage
    fetch(geometryUrl)
      .then((res) => res.json())
      .then((mazeData: VoronoiMazeData) => {
        // Use aspect ratio from payload (or mazeData) for non-square canvas sizing
        const aspectRatio = mazeData.aspectRatio ?? payloadAspectRatio;

        // Fit the SILHOUETTE (not the worker's square) into the real play area
        mazeData.aspectRatio = aspectRatio;
        const { w: canvasW, h: canvasH } = fitToPlayArea(mazeData);

        // Store original and re-carve (same as templates).
        // allNeighbors = post-contour-filter adjacency, so re-carve is safe.
        // Worker cells are sized for a phone; a big screen gets more of them.
        const sized = densifyForScreen(mazeData, difficulty);
        originalMazeDataRef.current = sized;
        if (args.geometryUrls) imageGeometryUrlsRef.current = args.geometryUrls;
        const recarvedData = recarveMaze(sized, { difficulty });

        const state: VoronoiMazeState = {
          mazeData: recarvedData,
          canvasSize: { width: Math.round(canvasW), height: Math.round(canvasH) },
          pathSegments: [],
          isCompleted: false,
          backgroundImageUrl: imageUrl,
        };

        // Determine which mode triggered this generation. Prefer the explicit
        // hint from the caller / the result's mazeType; else detect by phase.
        const isImageMode = args.isImage
          ?? (mazeType === 'image-maze'
            || (mazeType !== 'template-maze'
              && (imagePhase === 'processing' || settings.mazeMode === 'image')));
        if (isImageMode) {
          setImageVoronoiState(state);
          setImagePhase('playing');
          setImageError(null);
          // Preload background image for rendering
          if (imageUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              setImageVoronoiState(prev => prev ? { ...prev, backgroundImage: img } : null);
            };
            img.src = mediaSrc(imageUrl);
          }
        } else {
          setVoronoiMazeState(state);
          setTemplatePhase('playing');
          setTemplateError(null);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch maze geometry:', err);
        if (mazeType === 'image-maze') {
          setImageError('Failed to load maze');
          setImagePhase('idle');
        } else {
          setTemplateError('Failed to load maze');
          setTemplatePhase('idle');
        }
      });
  }, [settings.difficulty, imagePhase, settings.mazeMode]);

  // Route a generation failure/timeout error to the right mode's UI.
  const showGenerationError = useCallback((isImage: boolean, error: string) => {
    if (isImage) {
      setImageError(error);
      setImagePhase('idle');
    } else {
      setTemplateError(error);
      setTemplatePhase('idle');
    }
  }, []);

  // Poll fallback for readMazeResult — the SSE-independent path that guarantees a
  // spent token yields a persisted, retrievable maze (readMazeResult persists +
  // settles server-side) even if the SSE nudge is dropped or no consumer daemon
  // runs. A newer generation supersedes an in-flight poll via pollTokenRef; on
  // budget exhaustion we abandon (refund) so no token lingers without a result.
  const pollTokenRef = useRef(0);
  const startResultPolling = useCallback((mazeId: string, isImage: boolean) => {
    const myToken = ++pollTokenRef.current;
    const deadline = Date.now() + 45_000;
    const INTERVAL_MS = 2_500;

    const tick = async () => {
      if (pollTokenRef.current !== myToken) return; // superseded / cancelled

      let res: Awaited<ReturnType<typeof readMazeResult>>;
      try {
        res = await readMazeResult(mazeId);
      } catch {
        res = { status: 'pending' };
      }
      if (pollTokenRef.current !== myToken) return;

      if (res.status === 'completed') {
        // Persist + settle already happened server-side. Render only if the UI is
        // still waiting on THIS maze (SSE may have rendered it already).
        if (pendingMazeIdRef.current === mazeId) {
          pendingMazeIdRef.current = null;
          trackGenerated(mazeId, res.maze.mazeType, isImage);
          renderGeneratedMaze({
            geometryUrl: mediaSrc(res.maze.geometryBlobUrl),
            geometryUrls: res.maze.geometryUrls
              ? Object.fromEntries(Object.entries(res.maze.geometryUrls).map(([k, v]) => [k, mediaSrc(v)]))
              : undefined,
            mazeType: res.maze.mazeType,
            imageUrl: res.maze.sourceImageBlobUrl,
            aspectRatio: res.maze.aspectRatio,
            isImage,
          });
          if (isImage) refreshMazeQuota();
        }
        return;
      }
      if (res.status === 'failed') {
        if (pendingMazeIdRef.current === mazeId) {
          pendingMazeIdRef.current = null;
          showGenerationError(isImage, res.error || t('games.maze.serverError'));
        }
        return;
      }

      if (Date.now() >= deadline) {
        // Never charge without a result: release the hold on timeout.
        await abandonMazeGeneration(mazeId).catch(() => {});
        if (pendingMazeIdRef.current === mazeId) {
          pendingMazeIdRef.current = null;
          showGenerationError(isImage, t('games.maze.timeout'));
        }
        return;
      }
      setTimeout(tick, INTERVAL_MS);
    };

    setTimeout(tick, INTERVAL_MS);
  }, [renderGeneratedMaze, showGenerationError, t, trackGenerated]);

  // SSE: listen for maze generation completion (shared by template + image modes)
  useEventSourceEvent('games.maze.generated', useCallback((payload: Record<string, unknown>) => {
    const mazeId = payload.mazeId as string;
    if (mazeId !== pendingMazeIdRef.current) return;
    pendingMazeIdRef.current = null;
    trackGenerated(mazeId, payload.mazeType as string | undefined);
    renderGeneratedMaze({
      geometryUrl: payload.geometryUrl as string,
      geometryUrls: (payload.geometryUrls as Record<string, string> | undefined) ?? undefined,
      mazeType: payload.mazeType as string | undefined,
      imageUrl: payload.imageUrl as string | undefined,
      aspectRatio: (payload.aspectRatio as number) || 1.0,
    });
  }, [renderGeneratedMaze, trackGenerated]));

  // SSE: listen for maze generation failure
  useEventSourceEvent('games.maze.failed', useCallback((payload: Record<string, unknown>) => {
    const mazeId = payload.mazeId as string;
    if (mazeId !== pendingMazeIdRef.current) return;

    pendingMazeIdRef.current = null;
    const error = (payload.error as string) || 'Maze generation failed';
    const mazeType = payload.mazeType as string | undefined;
    console.error('Maze generation failed:', error);

    if (mazeType === 'image-maze') {
      setImageError(error);
      setImagePhase('idle');
    } else {
      setTemplateError(error);
      setTemplatePhase('idle');
    }
  }, []));

  // Таймер запускается при первом касании/клике в MazeCanvas

  // Получение следующей сложности
  const getNextDifficulty = (current: GameDifficulty): GameDifficulty | null => {
    const order: GameDifficulty[] = ['easy', 'medium', 'hard', 'expert'];
    const currentIndex = order.indexOf(current);
    return currentIndex < order.length - 1 ? order[currentIndex + 1] : null;
  };

  // Проверка, можно ли повысить сложность
  const canIncreaseDifficulty = getNextDifficulty(settings.difficulty) !== null;

  // Обработчики
  const handleNewGame = useCallback(() => {
    setShowSolution(false);
    // A photo or shape maze is on screen → "New game" = a NEW VARIANT of THAT
    // maze (same picture, same difficulty; re-carved walls, entry and exit) —
    // the player picked this shape on purpose. Only the free modes randomize.
    const variantOf = settings.mazeMode === 'image' ? imagePhase : settings.mazeMode === 'template' ? templatePhase : null;
    if (variantOf === 'playing' && originalMazeDataRef.current) {
      const recarved = recarveMaze(originalMazeDataRef.current, { difficulty: settings.difficulty });
      const reset = (prev: VoronoiMazeState | null) =>
        prev ? { ...prev, mazeData: recarved, pathSegments: [], isCompleted: false } : prev;
      if (settings.mazeMode === 'image') setImageVoronoiState(reset);
      else setVoronoiMazeState(reset);
      setIsCompleted(false);
      setGameResult(null);
      timer.reset();
      solutionPeeksRef.current = 0;
      return;
    }
    // Free modes: pick a random type
    const pick = pickRandomMode();
    updateSettings({ mazeMode: pick.mode, ...(pick.shapeStyle ? { shapeStyle: pick.shapeStyle } : {}) });
    if (pick.mode === 'template' && pick.templateId) {
      setIsCompleted(false);
      setGameResult(null);
      handleTemplateSelectRef.current(pick.templateId);
      return;
    }
    // Non-template modes (rectangular/radial): regenerate via MazeCanvas
    if (mazeRef.current) {
      mazeRef.current.regenerate();
      setIsCompleted(false);
      timer.reset();
      timer.start();
    }
  }, [timer, updateSettings, settings.mazeMode, settings.difficulty, imagePhase, templatePhase]);

  const handleMazeComplete = useCallback(() => {
    setIsCompleted(true);
    timer.stop();
    track('maze_completed', {
      maze_id: eventMazeId(settings.mazeMode),
      seconds: elapsedSeconds(timer.elapsedTime),
      hints: solutionPeeksRef.current,
    });

    // Создаем результат игры
    const result: GameResult = {
      gameId: 'maze',
      status: 'won',
      difficulty: settings.difficulty,
      time: timer.elapsedTime,
      hintsUsed: 0, // В лабиринте нет подсказок
      completedAt: new Date()
      // score убираем - количество сегментов не логично как счет
    };

    setGameResult(result);
  }, [timer, settings.difficulty, settings.mazeMode, track, eventMazeId]);

  const handleMazeRef = useCallback((ref: {
    regenerate: () => void;
    download: () => void;
    getState: () => any;
    getCanvas: () => HTMLCanvasElement | null;
  } | null) => {
    mazeRef.current = ref;
  }, []);

  const handlePathUpdate = useCallback(() => {
    // Убираем отслеживание сегментов - не нужно для лабиринта
  }, []);

  const handleGameStart = useCallback(() => {
    if (!timer.isRunning) {
      timer.start();
    }
  }, [timer]);

  const handleDownload = useCallback(() => {
    if (mazeRef.current) {
      mazeRef.current.download();
      track('maze_shared', { maze_id: eventMazeId(settings.mazeMode), channel: 'download_png' });
    }
  }, [track, eventMazeId, settings.mazeMode]);

  const handlePrint = useCallback(() => {
    if (!mazeRef.current) return;
    const state = mazeRef.current.getState();
    const canvas = mazeRef.current.getCanvas();
    if (!state || !canvas) return;

    // All modes use the same MazePrintPreview (with QR code, settings)
    setMazeState(state);
    setCanvasRef(canvas);
    setShowPrintPreview(true);
  }, []);

  // Обработчик изменения сложности
  const handleDifficultyChange = useCallback((difficulty: GameDifficulty) => {
    updateSettings({ difficulty });
    setShowSolution(false);
    // Update URL with new difficulty
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('difficulty', difficulty);
      window.history.pushState({}, '', url.toString());
    }

    // Image mode: every level was tessellated in the original (paid) job → just
    // fetch that level's geometry. Only mazes from before that fall back to a
    // re-queue (paid like a fresh image maze).
    if (settings.mazeMode === 'image' && imagePhase === 'playing') {
      const sourceImageUrl = imageVoronoiState?.backgroundImageUrl;
      // Keys from saved_mazes.geometry_urls are RAW storage keys — always route
      // through the media proxy (mediaSrc passes already-proxied urls through).
      const levelUrl = imageGeometryUrlsRef.current?.[difficulty];
      if (levelUrl) {
        setIsCompleted(false);
        setGameResult(null);
        setShowSolution(false);
        timer.reset();
        setImagePhase('processing');
        renderGeneratedMaze({
          geometryUrl: mediaSrc(levelUrl),
          geometryUrls: imageGeometryUrlsRef.current ?? undefined,
          mazeType: 'image-maze',
          imageUrl: sourceImageUrl,
          aspectRatio: imageVoronoiState?.mazeData.aspectRatio,
          isImage: true,
          difficulty,
        });
        return;
      }
      // Older saved photo mazes (one geometry, no per-level map): re-tessellate
      // the silhouette CONTOUR right here — different cell count for the new
      // level, fresh seeds → different entry/exit; recarveMaze guarantees a
      // solution path. Free and instant; no worker round-trip.
      const local = originalMazeDataRef.current?.contour
        ? tessellateFromContour(originalMazeDataRef.current.contour, difficulty, {
            aspectRatio: originalMazeDataRef.current.aspectRatio,
            geometryTransform: originalMazeDataRef.current.geometryTransform,
          })
        : null;
      if (local) {
        setIsCompleted(false);
        setGameResult(null);
        setShowSolution(false);
        timer.reset();
        originalMazeDataRef.current = local;
        const recarved = recarveMaze(local, { difficulty });
        setImageVoronoiState((prev) =>
          prev ? { ...prev, mazeData: recarved, pathSegments: [], isCompleted: false } : prev,
        );
        return;
      }
      if (sourceImageUrl) {
        setIsCompleted(false);
        setGameResult(null);
        setShowSolution(false);
        timer.reset();
        setImagePhase('processing');
        setImageVoronoiState(null);

        reEnqueueImageMaze(sourceImageUrl, difficulty).then((result) => {
          if (result.success) {
            pendingMazeIdRef.current = result.data.mazeId;
            startResultPolling(result.data.mazeId, true);
          } else {
            setImageError(result.error ?? 'Failed to re-generate maze');
            setImagePhase('idle');
          }
        });
        return;
      }
    }

    // If in template playing/processing mode, reload the same template
    if (settings.mazeMode === 'template' && selectedTemplateId && templatePhase !== 'idle') {
      setIsCompleted(false);
      setGameResult(null);
      timer.reset();

      const tpl = getTemplateById(selectedTemplateId);
      if (tpl?.configPath) {
        // Load different JSON for new difficulty — pass difficulty directly
        // (settings.difficulty may not be updated yet due to React batching)
        const configPath = getTemplateConfigPath(tpl, difficulty);
        if (configPath) {
          setTemplatePhase('processing');
          const { maxW, maxH } = measurePlayArea();
          loadTemplateConfig(configPath, maxW, maxH).then((state) => {
            const sized = densifyForScreen(state.mazeData, difficulty);
            originalMazeDataRef.current = sized;
            const recarved = recarveMaze(sized, { difficulty });
            setVoronoiMazeState({ ...state, mazeData: recarved });
            setTemplatePhase('playing');
          }).catch(() => {
            setTemplateError('Failed to load maze');
            setTemplatePhase('idle');
          });
        }
        return;
      }

      // Fallback: re-enqueue queue-based generation
      setTemplatePhase('processing');
      setVoronoiMazeState(null);

      const formData = new FormData();
      formData.set('templateId', selectedTemplateId);
      formData.set('difficulty', difficulty);

      enqueueMazeGeneration(formData).then((result) => {
        if (result.success) {
          pendingMazeIdRef.current = result.data.mazeId;
          startResultPolling(result.data.mazeId, false);
        } else {
          setTemplateError(result.error ?? 'Failed to re-generate maze');
          setTemplatePhase('idle');
        }
      });
    }
  }, [updateSettings, settings.mazeMode, selectedTemplateId, templatePhase, imagePhase, imageVoronoiState, timer, startResultPolling, renderGeneratedMaze]);

  const handleIncreaseDifficulty = useCallback(() => {
    const nextDifficulty = getNextDifficulty(settings.difficulty);
    if (nextDifficulty) {
      setGameResult(null);
      setIsCompleted(false);
      handleDifficultyChange(nextDifficulty);
    }
  }, [settings.difficulty, handleDifficultyChange]);

  // Обработчик изменения цвета
  const handleColorChange = useCallback((color: string) => {
    updateSettings({ lineColor: color });
  }, [updateSettings]);

  // Обработчик изменения режима лабиринта
  const handleModeChange = useCallback((mode: MazeMode) => {
    setIsExplicitMode(true);
    updateSettings({ mazeMode: mode });
    updateUrlParams({ mode });
    setIsCompleted(false);
    setGameResult(null);
    setShowSolution(false);
    timer.reset();
    pendingMazeIdRef.current = null;
    beginMaze(null);

    if (mode === 'template') {
      setTemplatePhase('idle');
      setVoronoiMazeState(null);
      setTemplateError(null);
    } else if (mode === 'image') {
      setImagePhase('idle');
      setImageVoronoiState(null);
      setImageError(null);
    } else {
      if (mazeRef.current) {
        mazeRef.current.regenerate();
      }
    }
  }, [updateSettings, timer, beginMaze]);

  // Handle image upload: enqueue image-based maze generation. No cost dialog —
  // the quota is shown as "N mazes left" and the server reserve is the gate; an
  // INSUFFICIENT_BALANCE reply flips the panel to the top-up state.
  const handleImageSelected = useCallback(async (file: File) => {
    setImagePhase('processing');
    setImageError(null);
    setImageVoronoiState(null);
    setShowSolution(false);
    timer.reset();

    const formData = new FormData();
    formData.set('image', file);
    formData.set('difficulty', settings.difficulty);

    const result = await enqueueMazeGeneration(formData);
    if (result.success) {
      pendingMazeIdRef.current = result.data.mazeId;
      startResultPolling(result.data.mazeId, true);
    } else if (result.code === 'INSUFFICIENT_BALANCE') {
      setMazeQuota(0);
      setImagePhase('idle');
    } else {
      setImageError(result.error ?? 'Failed to start maze generation');
      setImagePhase('idle');
    }
  }, [settings.difficulty, timer, startResultPolling, setMazeQuota]);

  // Reset image upload to idle state
  const handleImageRetry = useCallback(() => {
    setImagePhase('idle');
    setImageError(null);
    setImageVoronoiState(null);
    pendingMazeIdRef.current = null;
  }, []);

  // Handle saved maze selection: fetch geometry and render
  const handleSavedMazeSelect = useCallback((maze: SavedMaze) => {
    updateSettings({ mazeMode: 'image' });
    setImagePhase('processing');
    setImageError(null);
    setImageVoronoiState(null);
    setShowSolution(false);
    timer.reset();
    updateUrlParams({ mazeId: maze.id });

    fetch(mediaSrc(maze.geometryBlobUrl))
      .then((res) => res.json())
      .then((mazeData: VoronoiMazeData) => {
        const { w, h } = fitToPlayArea(mazeData);

        const source = geometryForLevel(mazeData, maze, settings.difficulty);
        originalMazeDataRef.current = source;
        imageGeometryUrlsRef.current = maze.geometryUrls ?? null;
        const recarved = recarveMaze(source, { difficulty: settings.difficulty });
        beginMaze(maze.id);

        setImageVoronoiState({
          mazeData: recarved,
          canvasSize: { width: w, height: h },
          pathSegments: [],
          isCompleted: false,
          backgroundImageUrl: maze.sourceImageBlobUrl || undefined,
        });
        setImagePhase('playing');

        // Preload background image
        if (maze.sourceImageBlobUrl) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            setImageVoronoiState(prev => prev ? { ...prev, backgroundImage: img } : null);
          };
          // storage KEY → media proxy (a bare key resolved relative to the page → 404)
          img.src = mediaSrc(maze.sourceImageBlobUrl);
        }
      })
      .catch((err) => {
        console.error('Failed to load saved maze:', err);
        setImageError('Failed to load saved maze');
        setImagePhase('idle');
      });
  }, [timer, settings.difficulty, beginMaze]);

  // Return from image playing to upload
  const handleBackToUpload = useCallback(() => {
    setImagePhase('idle');
    setImageVoronoiState(null);
    setIsCompleted(false);
    setGameResult(null);
    setShowSolution(false);
    timer.reset();
    pendingMazeIdRef.current = null;
  }, [timer]);

  // Handle template selection: use pre-computed config when available, otherwise enqueue
  const handleTemplateSelect = useCallback(async (templateId: string) => {
    setSelectedTemplateId(templateId);
    setTemplateError(null);
    setVoronoiMazeState(null);
    setShowSolution(false);
    timer.reset();
    updateUrlParams({ mode: 'template', templateId, difficulty: settings.difficulty });

    const tpl = getTemplateById(templateId);
    const configPath = tpl ? getTemplateConfigPath(tpl, settings.difficulty) : undefined;
    if (configPath) {
      // Direct load from pre-computed JSON — no queue, no SSE
      // Different difficulty = different JSON (more/fewer cells)
      setTemplatePhase('processing');
      try {
        // Size from the real play area
        const { maxW, maxH } = measurePlayArea();
        const state = await loadTemplateConfig(configPath, maxW, maxH);
        // Store original maze data for future re-carves (new game = same cells, different path)
        // (cells sized for the screen — the JSON is phone-sized)
        const sized = densifyForScreen(state.mazeData, settings.difficulty);
        originalMazeDataRef.current = sized;
        // Re-carve to randomize walls and entry/exit
        const recarved = recarveMaze(sized, { difficulty: settings.difficulty });
        setVoronoiMazeState({ ...state, mazeData: recarved });
        setTemplatePhase('playing');
        // A pre-computed shape maze is the shape flow's success — same funnel step as a
        // worker-generated one; its id is the template id (our object, not PII).
        beginMaze(templateId);
        track('maze_generated', { maze_id: templateId, kind: 'shape', difficulty: settings.difficulty, gift: false });
      } catch (err) {
        console.error('Failed to load template config:', err);
        setTemplateError('Failed to load maze');
        setTemplatePhase('idle');
      }
      return;
    }

    // Fallback: queue-based generation for templates without configPath
    setTemplatePhase('processing');

    const formData = new FormData();
    formData.set('templateId', templateId);
    formData.set('difficulty', settings.difficulty);

    const result = await enqueueMazeGeneration(formData);
    if (result.success) {
      pendingMazeIdRef.current = result.data.mazeId;
      startResultPolling(result.data.mazeId, false);
    } else {
      console.error('Failed to enqueue maze:', result.error);
      setTemplateError(result.error ?? 'Failed to start maze generation');
      setTemplatePhase('idle');
    }
  }, [settings.difficulty, timer, startResultPolling, beginMaze, track]);

  // Keep ref in sync so early callers (useEffect, handleNewGame) can reach it
  handleTemplateSelectRef.current = handleTemplateSelect;

  // Return to template gallery
  const handleBackToGallery = useCallback(() => {
    setTemplatePhase('idle');
    setVoronoiMazeState(null);
    setIsCompleted(false);
    setGameResult(null);
    setShowSolution(false);
    timer.reset();
    pendingMazeIdRef.current = null;
  }, [timer]);

  // Настройки игры — mode + difficulty are now surfaced in the play area
  // (discoverability), so the gear keeps only the advanced/secondary options.
  // The renderer reads the opacity from the state object; derive it so a slider
  // move re-renders the canvas (MazeCanvas re-renders on prop identity).
  const imageVoronoiStateWithOpacity = useMemo(
    () => (imageVoronoiState ? { ...imageVoronoiState, backgroundOpacity: settings.imageOpacity } : null),
    [imageVoronoiState, settings.imageOpacity],
  );

  const gameSettings = (
    <div className="space-y-4">
      <DifficultySelector
        difficulty={settings.difficulty}
        onDifficultyChange={handleDifficultyChange}
      />

      <MazeColorSelector
        selectedColor={settings.lineColor}
        onColorChange={handleColorChange}
      />

      {/* Photo mazes: how strongly the source photo shows through under the cells. */}
      {settings.mazeMode === 'image' && imageVoronoiState?.backgroundImageUrl && (
        <div className="space-y-2" data-testid="maze-photo-opacity">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">{t('games.maze.photoOpacity')}</span>
            <span className="tabular-nums text-muted-foreground">{Math.round(settings.imageOpacity * 100)}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={Math.round(settings.imageOpacity * 100)}
            onChange={(e) => updateSettings({ imageOpacity: Number(e.target.value) / 100 })}
            className="w-full accent-primary"
            aria-label={t('games.maze.photoOpacity')}
            data-testid="maze-photo-opacity-input"
          />
        </div>
      )}
    </div>
  );

  // Статистика игры - упрощенная для лабиринта
  const gameStats = {
    target: 1, // Цель - завершить лабиринт
    score: isCompleted ? 1 : 0 // Счет - завершен или нет
    // moves убираем - не информативно для лабиринта
  };

  // Whether an async mode (template/image) is not yet playing
  const isTemplateNonPlaying = settings.mazeMode === 'template' && templatePhase !== 'playing';
  const isImageNonPlaying = settings.mazeMode === 'image' && imagePhase !== 'playing';
  const isAsyncNonPlaying = isTemplateNonPlaying || isImageNonPlaying;

  // Дополнительные кнопки для панели
  const extraActions = (
    <>
      {settings.mazeMode === 'template' && templatePhase === 'playing' && (
        <Button
          data-testid="maze-back-to-gallery"
          variant="outline"
          size="sm"
          onClick={handleBackToGallery}
          className="gap-1 sm:gap-2 hover:bg-white/50 h-7 w-7 sm:h-auto sm:w-auto p-0 sm:px-3"
        >
          <Shapes className="h-4 w-4" />
          <span className="hidden sm:inline">{t('games.maze.modeTemplate')}</span>
        </Button>
      )}
      {settings.mazeMode === 'image' && imagePhase === 'playing' && (
        <Button
          data-testid="maze-back-to-upload"
          variant="outline"
          size="sm"
          onClick={handleBackToUpload}
          className="gap-1 sm:gap-2 hover:bg-white/50 h-7 w-7 sm:h-auto sm:w-auto p-0 sm:px-3"
        >
          <ImageIcon className="h-4 w-4" />
          <span className="hidden sm:inline">{t('games.maze.modeImage')}</span>
        </Button>
      )}
      {(settings.mazeMode === 'template' || settings.mazeMode === 'image') &&
        (templatePhase === 'playing' || imagePhase === 'playing') && (
        <Button
          data-testid="maze-show-solution"
          variant={showSolution ? 'default' : 'outline'}
          size="sm"
          onClick={toggleSolution}
          className="gap-1 sm:gap-2 hover:bg-white/50 h-7 w-7 sm:h-auto sm:w-auto p-0 sm:px-3"
        >
          <Lightbulb className="h-4 w-4" />
          <span className="hidden sm:inline">{t('games.maze.showSolution')}</span>
        </Button>
      )}
      <Button
        data-testid="maze-print-button"
        variant="outline"
        size="sm"
        onClick={handlePrint}
        className="gap-1 sm:gap-2 hover:bg-white/50 h-7 w-7 sm:h-auto sm:w-auto p-0 sm:px-3 hidden sm:flex"
      >
        <Printer className="h-4 w-4" />
        <span className="hidden sm:inline">{t('games.maze.print.printNow')}</span>
      </Button>
      <Button
        data-testid="maze-download-button"
        variant="outline"
        size="sm"
        onClick={handleDownload}
        className="gap-1 sm:gap-2 hover:bg-white/50 h-7 w-7 sm:h-auto sm:w-auto p-0 sm:px-3 hidden sm:flex"
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">{t('common.download')}</span>
      </Button>
    </>
  );

  return (
    <>
      <GameLayout
        gameId="maze"
        title={t('games.maze.title')}
        settings={gameSettings}
        timer={timer}
        stats={gameStats}
        onNewGame={handleNewGame}
        onShowRules={() => setIsRulesOpen(true)}
        extraActions={extraActions}
      >
        <div className="w-full h-full flex flex-col">
          {/* Discoverable capability bar — modes were previously buried in the
              settings gear where a new user would never find them. */}
          <div className="shrink-0 flex flex-col items-center gap-2 px-2 pt-1 pb-2">
            <MazeModeSelector
              mode={settings.mazeMode}
              onModeChange={handleModeChange}
            />
            {isAsyncNonPlaying && (
              <DifficultySelector
                difficulty={settings.difficulty}
                onDifficultyChange={handleDifficultyChange}
              />
            )}
          </div>

          {/* First-visit hint pointing at the surfaced capabilities. */}
          {showCapabilityHint && isAsyncNonPlaying && (
            <div
              data-testid="maze-caps-hint"
              className="shrink-0 mx-auto mb-2 flex max-w-xl items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-left"
            >
              <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="flex-1">
                <p className="text-xs font-medium text-foreground">
                  {t('games.maze.setupHintTitle')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('games.maze.setupHint')}
                </p>
              </div>
              <Button
                data-testid="maze-caps-hint-dismiss"
                variant="ghost"
                size="sm"
                onClick={dismissCapabilityHint}
                className="h-6 shrink-0 px-2 text-xs"
              >
                {t('games.maze.dismissHint')}
              </Button>
            </div>
          )}

          <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden" data-testid="maze-play-area">
        {settings.mazeMode === 'template' && templatePhase === 'idle' && (
          <div className="w-full h-full flex flex-col items-center justify-start p-4 gap-4 overflow-y-auto">
            {templateError && (
              <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                {templateError}
              </div>
            )}
            <TemplateGallery onTemplateSelect={handleTemplateSelect} />
          </div>
        )}

        {settings.mazeMode === 'template' && templatePhase === 'processing' && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t('games.maze.generating')}</p>
          </div>
        )}

        {settings.mazeMode === 'image' && imagePhase === 'idle' && !isAuthenticated && (
          <div className="w-full h-full flex flex-col items-center justify-center p-4 gap-6 overflow-y-auto">
            <div className="flex flex-col items-center gap-3 text-center">
              <LogIn className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t('games.maze.imageAuthRequired')}
              </p>
              <Button
                data-testid="maze-image-signin"
                variant="default"
                size="sm"
                onClick={() => signIn()}
              >
                {t('signin.title')}
              </Button>
            </div>
          </div>
        )}

        {settings.mazeMode === 'image' && imagePhase === 'idle' && isAuthenticated && (
          <div className="w-full h-full flex flex-col items-center justify-start p-4 gap-4 overflow-y-auto">
            {/* Hidden tokens: the quota reads in MAZES. A brand-new player sees the
                gift; a spent gift becomes a top-up panel (BuyTokensWidget, mounted
                in the root layout). Unknown (null) lets the upload through. */}
            {typeof mazeQuota === 'number' && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <div
                  data-testid="maze-image-quota"
                  className="flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1 text-sm"
                >
                  <Gift className="h-4 w-4 text-amber-500" />
                  <span className="font-medium">
                    {t('games.maze.imageQuota', { count: mazeQuota })}
                  </span>
                </div>
                {/* Always reachable — not only once the gift is spent. */}
                <Button size="sm" variant="outline" onClick={() => openBuyTokens('games')} data-testid="maze-image-topup-button">
                  {t('games.maze.topUp')}
                </Button>
              </div>
            )}
            {mazeQuota === 0 ? (
              <div
                data-testid="maze-image-topup"
                className="flex w-full max-w-lg flex-col items-center gap-3 rounded-lg border border-dashed border-border p-6 text-center"
              >
                <p className="text-sm text-muted-foreground">
                  {t('games.maze.imageQuotaEmpty', { gift: giftMazes ?? 0 })}
                </p>
                <Button size="sm" onClick={() => openBuyTokens('games')}>
                  {t('games.maze.topUp')}
                </Button>
              </div>
            ) : (
              <ImageUploadPanel
                onImageSelected={handleImageSelected}
                isProcessing={false}
                error={imageError}
                onRetry={handleImageRetry}
              />
            )}
            <SavedMazesGallery
              onMazeSelect={handleSavedMazeSelect}
              className="w-full max-w-lg"
            />
          </div>
        )}

        {settings.mazeMode === 'image' && imagePhase === 'processing' && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{t('games.maze.processing')}</p>
            <p className="text-xs text-muted-foreground">{t('games.maze.removeBackground')}</p>
          </div>
        )}

        {(!isAsyncNonPlaying) && (
          <MazeCanvas
            difficulty={settings.difficulty}
            lineColor={settings.lineColor}
            mode={settings.mazeMode}
            voronoiMazeData={
              settings.mazeMode === 'template'
                ? voronoiMazeState
                : settings.mazeMode === 'image'
                  ? imageVoronoiStateWithOpacity
                  : undefined
            }
            solutionPath={solutionPath}
            onRef={handleMazeRef}
            onComplete={handleMazeComplete}
            onPathUpdate={handlePathUpdate}
            onGameStart={handleGameStart}
          />
        )}
          </div>
        </div>
      </GameLayout>

      <GameRulesModal
        isOpen={isRulesOpen}
        onClose={() => setIsRulesOpen(false)}
        gameTitle={t('games.maze.rules.title')}
        rulesContent={<MazeRules />}
      />

      <GameVictoryModal
        isOpen={isCompleted && !!gameResult}
        result={gameResult || {
          gameId: 'maze',
          status: 'won',
          difficulty: settings.difficulty,
          time: 0,
          hintsUsed: 0,
          completedAt: new Date()
        }}
        onNewGame={() => {
          setGameResult(null);
          setShowSolution(false);
          if (settings.mazeMode === 'template' && selectedTemplateId) {
            // Re-carve from stored original data for instant new maze
            if (originalMazeDataRef.current) {
              const recarved = recarveMaze(originalMazeDataRef.current, { difficulty: settings.difficulty });
              setVoronoiMazeState((prev) => prev ? { ...prev, mazeData: recarved, pathSegments: [], isCompleted: false } : null);
              setIsCompleted(false);
              timer.reset();
            } else {
              // Fallback: reload template if original data not available
              handleTemplateSelect(selectedTemplateId);
            }
          } else if (settings.mazeMode === 'image' && originalMazeDataRef.current) {
            // Re-carve for new game (different passages + entry/exit)
            const recarved = recarveMaze(originalMazeDataRef.current, { difficulty: settings.difficulty });
            setImageVoronoiState((prev) => prev ? { ...prev, mazeData: recarved, pathSegments: [], isCompleted: false } : null);
            setIsCompleted(false);
            timer.reset();
          } else {
            handleNewGame();
          }
        }}
        onIncreaseDifficulty={handleIncreaseDifficulty}
        canIncreaseDifficulty={canIncreaseDifficulty}
        onClose={() => {
          setGameResult(null);
          setIsCompleted(false);
          if (settings.mazeMode === 'template') {
            handleBackToGallery();
          } else if (settings.mazeMode === 'image') {
            handleBackToUpload();
          }
        }}
        showDifficultyButton={false}
        showBackButton={false}
        showHints={false}
      />

      {mazeState && canvasRef && (
        <MazePrintPreview
          isOpen={showPrintPreview}
          mazeState={mazeState}
          canvasRef={canvasRef}
          onClose={() => setShowPrintPreview(false)}
          onShare={(channel) => track('maze_shared', { maze_id: eventMazeId(settings.mazeMode), channel })}
        />
      )}
    </>
  );
}
